import { api } from '../../lib/api';
import type {
  BestandsAsset,
  Channel,
  CockpitDaten,
  KalenderAntwort,
  PostStatus,
  ReviewRevision,
  ReviewRevisionDetail,
  ReviewTarget,
  Rhythmus,
} from './types';

/**
 * Schluessel nach Hausmuster (`['articles', {filters}]` / `['article', id]`).
 *
 * Zentral hier, nicht je Aufrufstelle: Eine Mutation, die den falschen
 * Schluessel invalidiert, laesst die Ansicht still veraltet stehen — und man
 * sucht den Fehler dann in der Mutation, nicht im Schluessel.
 */
export const socialKeys = {
  calendar: (from: string, to: string, channel?: Channel, slots?: boolean, q?: string) =>
    ['social', 'calendar', { from, to, channel: channel ?? null, slots: slots ?? true, q: q ?? null }] as const,
  post: (id: string) => ['social', 'post', id] as const,
  assets: (unscheduled: boolean) => ['social', 'assets', { unscheduled }] as const,
  rules: () => ['social', 'rules'] as const,
};

export function fetchCalendar(opts: {
  from: string;
  to: string;
  channel?: Channel;
  slots?: boolean;
  /** Suche über alle Monate (CUR-2287) — mit ihr gelten `from`/`to` nicht. */
  q?: string;
}): Promise<KalenderAntwort> {
  const q = new URLSearchParams({ from: opts.from, to: opts.to });
  if (opts.channel) q.set('channel', opts.channel);
  if (opts.slots === false) q.set('slots', 'false');
  if (opts.q) q.set('q', opts.q);
  return api.get<KalenderAntwort>(`/admin/social/calendar?${q.toString()}`);
}

export function fetchPost(id: string): Promise<CockpitDaten> {
  return api.get<CockpitDaten>(`/admin/social/posts/${id}`);
}

export function patchPost(
  id: string,
  patch: {
    status?: PostStatus;
    /** `null` nimmt den Termin heraus (CUR-2247). */
    scheduledFor?: string | null;
    postedUrl?: string | null;
    checkedItems?: string[];
    note?: string | null;
    caption?: string;
    /** `null` = ohne Titel (CUR-2249). */
    title?: string | null;
    /** YouTube-Tags (CUR-2295); `[]` = ohne Tags. */
    tags?: string[];
  },
): Promise<unknown> {
  return api.patch(`/admin/social/posts/${id}`, patch);
}

/**
 * Kennzeichnung eines Beitrags — gilt für alle Fassungen (CUR-2249). Der Ordner nur bei
 * Entwürfen (CUR-2289): Kürzel oder Pfad, `null` nimmt ihn heraus.
 */
export function patchAsset(id: string, patch: { aiLabel?: boolean | null; advertising?: boolean | null; ordner?: string | null }): Promise<unknown> {
  return api.patch(`/admin/social/assets/${id}`, patch);
}

export function createReviewDraft(body: { channel: ReviewTarget; title: string; caption: string; scheduledFor?: string; aiLabel?: boolean; ordner?: string }): Promise<{ id: string; ergebnis: string }> {
  return api.post('/admin/social/review/drafts', body);
}

export function requestReviewUpload(postId: string, file: File, kind: 'VIDEO' | 'IMAGE' | 'COVER', position: number): Promise<{ id: string; url: string; headers: Record<string, string> }> {
  return api.post(`/admin/social/posts/${postId}/media`, { mimeType: file.type, bytes: file.size, kind, position });
}

export function completeReviewUpload(postId: string, mediaId: string): Promise<unknown> {
  return api.post(`/admin/social/posts/${postId}/media/${mediaId}/complete`);
}

export function deleteReviewMedia(postId: string, mediaId: string): Promise<unknown> {
  return api.delete(`/admin/social/posts/${postId}/media/${mediaId}`);
}

export function reorderReviewMedia(postId: string, ids: string[]): Promise<unknown> {
  return api.post(`/admin/social/posts/${postId}/media/order`, { ids });
}

/** Übernimmt die Medien einer anderen Fassung desselben Beitrags — kein zweiter Upload (CUR-2249). */
export function adoptReviewMedia(postId: string, fromPostId: string): Promise<unknown> {
  return api.post(`/admin/social/posts/${postId}/media/adopt`, { fromPostId });
}

export function fetchReviewMediaUrl(postId: string, mediaId: string): Promise<{ url: string }> {
  return api.get(`/admin/social/posts/${postId}/media/${mediaId}/url`);
}

export function fetchReviewRevision(postId: string, revisionId: string): Promise<ReviewRevisionDetail> {
  return api.get(`/admin/social/posts/${postId}/review/revisions/${revisionId}`);
}

export function submitReview(postId: string): Promise<ReviewRevision> {
  return api.post(`/admin/social/posts/${postId}/review/submit`);
}

export function retryReviewEmail(postId: string): Promise<ReviewRevision> {
  return api.post(`/admin/social/posts/${postId}/review/retry-email`);
}

export function withdrawReview(postId: string): Promise<unknown> {
  return api.post(`/admin/social/posts/${postId}/review/withdraw`);
}

export function deleteReviewPost(postId: string): Promise<unknown> {
  return api.delete(`/admin/social/posts/${postId}/review`);
}

export function createPost(body: {
  assetId: string;
  channel: Channel;
  scheduledFor: string;
  note?: string;
}): Promise<{ id: string }> {
  return api.post<{ id: string }>('/admin/social/posts', body);
}

export function fetchAssets(unscheduled: boolean): Promise<{ assets: BestandsAsset[] }> {
  return api.get<{ assets: BestandsAsset[] }>(
    `/admin/social/assets${unscheduled ? '?unscheduled=true' : ''}`,
  );
}

export function fetchRules(): Promise<{ rules: Rhythmus[] }> {
  return api.get<{ rules: Rhythmus[] }>('/admin/social/rules');
}

export function createRule(body: {
  stream: string;
  channel: Channel;
  weekdays: number[];
  startsOn: string;
  endsOn?: string | null;
}): Promise<Rhythmus> {
  return api.post<Rhythmus>('/admin/social/rules', body);
}

export function patchRule(
  id: string,
  patch: { weekdays?: number[]; endsOn?: string | null; skippedDates?: string[]; active?: boolean },
): Promise<Rhythmus> {
  return api.patch<Rhythmus>(`/admin/social/rules/${id}`, patch);
}

export function deleteRule(id: string): Promise<unknown> {
  return api.delete(`/admin/social/rules/${id}`);
}
